<template>
  <div class="rw">
    <ContentSkeleton v-if="loading && !items.length" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <ReportWidgetView
      v-else
      :cfg="cfg"
      :items="items"
      :fields="fields"
      :numeric-fields="numericFields"
      :total="total"
      :model-name="modelName"
      :full-report-path="fullReportPath"
    />

    <ReportWidgetSettings
      v-model="settingsOpen"
      :title="entry.title || ''"
      :config="cfg"
      :fields="fields"
      :x-fields="xFields"
      :numeric-fields="numericFields"
      @draft="draft = $event"
      @save="saveSettings"
    >
      <template #preview>
        <ReportWidgetView
          v-if="draft && items.length"
          :cfg="draft"
          :items="items"
          :fields="fields"
          :numeric-fields="numericFields"
          :total="total"
          :model-name="modelName"
          :link-rows="false"
        />
        <p v-else-if="loading" class="dw-empty">{{ $t("dashboardWidgets.catalog.loading") }}</p>
        <p v-else class="dw-empty">{{ $t("dashboardWidgets.report.empty") }}</p>
      </template>
    </ReportWidgetSettings>
  </div>
</template>

<script>
/**
 * Un reporte guardado (o un query report) en formato widget: lista, total o gráfico
 * (columnas, barras, línea, área, torta, dona) con eje X / eje Y configurables. No es
 * la tabla de /dynamic-model; el reporte completo queda a un click («Abrir reporte»).
 */
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import ReportWidgetView from "./ReportWidgetView.vue";
import ReportWidgetSettings from "./ReportWidgetSettings.vue";
import { getModelDefinitions, resolveFieldPath, CHART_TYPES, DATE_BUCKETS, AGGREGATIONS } from "../widgetData";

const LIST_LIMIT = 30;
// Total y gráfico cuentan sobre las filas traídas: hasta acá (el aviso lo dice).
const AGG_LIMIT = 500;
const SKIP_KEYS = new Set(["_id", "__v", "createdBy", "updatedBy", "deleted", "deletedAt"]);
const NOT_GROUPABLE = ["number", "currency", "file", "group"];

export default {
  name: "ReportWidget",
  components: { ContentSkeleton, ReportWidgetView, ReportWidgetSettings },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      error: false,
      items: [],
      total: 0,
      columnKeys: [],
      defs: [],
      settingsOpen: false,
      draft: null,
    };
  },
  computed: {
    modelName() {
      return this.entry.modelName || "";
    },
    fields() {
      return this.columnKeys.map((key) =>
        this.modelName ? resolveFieldPath(this.defs, this.modelName, key) : { key, label: key, type: guessType(this.items, key) }
      );
    },
    numericFields() {
      return this.fields.filter((f) => f.type === "number" || f.type === "currency");
    },
    /** Lo que sirve de eje X: categorías y fechas (no números sueltos ni archivos). */
    xFields() {
      return this.fields.filter((f) => !NOT_GROUPABLE.includes(f.type));
    },
    /**
     * Config completa con defaults. Acepta la de antes (`mode: "bars"` +
     * `groupField`), que pasa a gráfico de barras horizontales.
     */
    cfg() {
      const c = this.config || {};
      const legacyBars = c.mode === "bars";
      const mode = legacyBars ? "chart" : ["list", "kpi", "chart"].includes(c.mode) ? c.mode : "list";
      const chart = { ...(c.chart || {}) };
      const report = this.entry.report || {};
      const xKeys = this.xFields.map((f) => f.key);
      const categories = this.xFields.filter((f) => f.type !== "date");
      const preferredX = [chart.x, c.groupField, report.groupByField].find((k) => k && xKeys.includes(k));
      const fallbackX = categories[1] || categories[0] || this.xFields[0] || null;
      const y = this.numericFields.some((f) => f.key === chart.y) ? chart.y : null;
      return {
        mode,
        chart: {
          type: CHART_TYPES.includes(chart.type) ? chart.type : legacyBars ? "bar" : "column",
          x: preferredX || (fallbackX && fallbackX.key) || null,
          xDate: DATE_BUCKETS.includes(chart.xDate) ? chart.xDate : "month",
          agg: AGGREGATIONS.includes(chart.agg) && (chart.agg === "count" || y) ? chart.agg : "count",
          y,
          limit: Number(chart.limit) > 0 ? Number(chart.limit) : 8,
          sort: chart.sort === "label" ? "label" : "value",
        },
        list: {
          title: c.list && c.list.title ? c.list.title : null,
          details: Array.isArray(c.list && c.list.details) ? c.list.details.slice(0, 4) : [],
        },
        kpi: { fields: Array.isArray(c.kpi && c.kpi.fields) ? c.kpi.fields.slice(0, 4) : [] },
      };
    },
    fullReportPath() {
      if (this.config.reportId && this.modelName) {
        return `/dynamic-model/${this.modelName}?reportId=${this.config.reportId}`;
      }
      if (this.config.viewId) return `/view/${this.config.viewId}`;
      return null;
    },
    menuItems() {
      const mode = this.cfg.mode;
      const items = [
        { key: "mode:list", label: this.$tn("dashboardWidgets.report.modeList"), icon: "list", active: mode === "list" },
        { key: "mode:kpi", label: this.$tn("dashboardWidgets.report.modeKpi"), icon: "hashtag", active: mode === "kpi" },
        { key: "mode:chart", label: this.$tn("dashboardWidgets.report.modeChart"), icon: "chart-bar", active: mode === "chart" },
        { key: "configure", label: this.$tn("dashboardWidgets.report.configure"), icon: "sliders-h" },
      ];
      if (this.fullReportPath) {
        items.push({ divider: true, key: "d2" });
        items.push({ key: "open", label: this.$tn("dashboardWidgets.report.open"), icon: "external-link-alt" });
      }
      return items;
    },
    /** La lista pide pocas filas; total y gráfico, más (también mientras se configura). */
    wantsMany() {
      return this.cfg.mode !== "list" || this.settingsOpen;
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
    wantsMany(many) {
      if (many && (this.loadedLimit || 0) < AGG_LIMIT) this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      const limit = this.wantsMany ? AGG_LIMIT : LIST_LIMIT;
      try {
        if (this.modelName && !this.defs.length) this.defs = await getModelDefinitions();
        if (this.config.reportId) {
          const { data } = await wencesApi.get(`/api/gotrading/expanded/report/${this.config.reportId}`, {
            params: { limit },
          });
          this.items = (data && data.items) || [];
          const meta = (data && data._meta) || {};
          this.total = meta.total || this.items.length;
          this.columnKeys = (meta.columnOrder && meta.columnOrder.length ? meta.columnOrder : this.guessColumns()).slice(0, 16);
        } else if (this.config.viewId) {
          const { data } = await wencesApi.get(`/api/gotrading/views/${this.config.viewId}/combined-items`, {
            params: { page: 1, perPage: limit },
          });
          this.items = (data && data.items) || [];
          this.total = (data && data.pagination && data.pagination.total) || this.items.length;
          this.columnKeys = this.guessColumns().slice(0, 16);
        }
        this.loadedLimit = limit;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    guessColumns() {
      const first = this.items[0] || {};
      return Object.keys(first).filter((k) => !SKIP_KEYS.has(k));
    },
    saveSettings(next) {
      this.$emit("update-config", { mode: next.mode, chart: next.chart, list: next.list, kpi: next.kpi });
    },
    onMenu(key) {
      if (key.startsWith("mode:")) this.$emit("update-config", { mode: key.slice(5) });
      else if (key === "configure") this.settingsOpen = true;
      else if (key === "open" && this.fullReportPath) this.$router.push(this.fullReportPath);
    },
  },
};

/** Query reports: sin definición de modelo, el tipo se adivina por los valores. */
function guessType(items, key) {
  const sample = items
    .slice(0, 20)
    .map((it) => it[key])
    .filter((v) => v !== null && v !== undefined && v !== "");
  if (!sample.length) return "string";
  if (sample.every((v) => typeof v === "number")) return "number";
  if (sample.every((v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v))) return "date";
  return "string";
}
</script>

<style scoped>
.rw { height: 100%; display: flex; flex-direction: column; min-height: 0; }
</style>
